import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  apiPost,
  castErrorResult,
  companyIdSelector,
  createErrorResultFromException,
  createErrorResultWithNoErrorDetails,
  createSuccessResult,
  getPassengersPhonesInUsePath,
  Log,
  normalizeErrors,
  simpleResultErrorEmptyResponse,
  type IApiError,
  type IPassengerPhonesInUseRequestDto,
  type IPassengerPhonesInUseResponseDto,
  type SimpleResult,
} from "@ryde/shared";

export const usePassengersPhonesInUse = () => {
  const companyId = useRecoilValue(companyIdSelector);
  const { t } = useTranslation();

  const fetchPhonesInUse = useEvent(
    async (
      payload: IPassengerPhonesInUseRequestDto,
    ): Promise<SimpleResult<IPassengerPhonesInUseResponseDto>> => {
      if (!companyId) {
        Log.warn("fetchPhonesInUse aborted: companyId is not loaded yet");
        return createErrorResultWithNoErrorDetails("Company is not loaded yet");
      }

      try {
        const response = await apiPost<
          SimpleResult<IPassengerPhonesInUseResponseDto>
        >(getPassengersPhonesInUsePath(companyId), {
          body: { phones: payload.phones },
        });

        if (!response) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!response.success || !response.data) {
          return castErrorResult(response);
        }

        return createSuccessResult(response.data);
      } catch (error) {
        return createErrorResultFromException(
          normalizeErrors(error as IApiError, t),
        );
      }
    },
  );

  return { fetchPhonesInUse };
};
